import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PenTool, Calendar, MapPin, Clock, CheckCircle2, Circle, Video, Users,
  ChevronDown, Plus, X, AlertTriangle, FileText, Eye, Stamp, UserCheck,
  ClipboardCheck, MessageSquare, ChevronRight, Trash2, Edit3, Shield,
  VideoOff, Check
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Step {
  key: string;
  label: string;
  completed: boolean;
  completedAt: Date | null;
  notes: string | null;
}

interface Phase {
  key: string;
  label: string;
  status: "pending" | "in_progress" | "completed" | "skipped";
  completedAt: Date | null;
  completedBy: string | null;
  notes: string | null;
  steps: Step[];
}

interface Attendee {
  name: string;
  role: string;
  present: boolean;
}

interface SigningCeremony {
  status: "not_scheduled" | "scheduled" | "in_progress" | "completed" | "cancelled";
  scheduledDate: string | null;
  scheduledTime: string | null;
  location: string | null;
  phases: Phase[];
  attendees: Attendee[];
  videoRecorded: boolean;
  videoConsentObtained: boolean;
  ceremonyNotes: string | null;
  completedAt: Date | null;
}

interface Props {
  matterId: string;
  caseData: any;
  onUpdate: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  not_scheduled: { label: "Not Scheduled", color: "text-slate-600", bg: "bg-slate-100", border: "border-slate-200" },
  scheduled:     { label: "Scheduled",     color: "text-blue-700",  bg: "bg-blue-50",   border: "border-blue-200" },
  in_progress:   { label: "In Progress",   color: "text-amber-700", bg: "bg-amber-50",  border: "border-amber-200" },
  completed:     { label: "Completed",     color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  cancelled:     { label: "Cancelled",     color: "text-red-700",   bg: "bg-red-50",    border: "border-red-200" },
};

const PHASE_STATUS_CONFIG: Record<string, { color: string; bg: string; ringColor: string; label: string }> = {
  pending:     { color: "text-slate-400", bg: "bg-slate-100", ringColor: "ring-slate-200", label: "Pending" },
  in_progress: { color: "text-blue-600",  bg: "bg-blue-100",  ringColor: "ring-blue-300",  label: "In Progress" },
  completed:   { color: "text-emerald-600", bg: "bg-emerald-100", ringColor: "ring-emerald-300", label: "Completed" },
  skipped:     { color: "text-slate-400",  bg: "bg-slate-50",  ringColor: "ring-slate-200", label: "Skipped" },
};

const PHASE_ICONS: Record<string, any> = {
  pre_ceremony_prep:    ClipboardCheck,
  identity_verification: UserCheck,
  capacity_confirmation: Shield,
  document_review:       FileText,
  execution:             PenTool,
  witnessing:            Eye,
  post_execution:        Stamp,
};

const ATTENDEE_ROLES = [
  "Testator", "Attorney", "Witness 1", "Witness 2", "Notary", "Interpreter",
];

const DEFAULT_PHASES: Phase[] = [
  {
    key: "pre_ceremony_prep",
    label: "Pre-Ceremony Preparation",
    status: "pending",
    completedAt: null,
    completedBy: null,
    notes: null,
    steps: [
      { key: "confirm_docs_final", label: "Confirm all documents are in final form", completed: false, completedAt: null, notes: null },
      { key: "verify_witness_availability", label: "Verify witness availability and independence", completed: false, completedAt: null, notes: null },
      { key: "confirm_notary", label: "Confirm notary availability (if required)", completed: false, completedAt: null, notes: null },
      { key: "prepare_room", label: "Prepare signing room/environment", completed: false, completedAt: null, notes: null },
      { key: "print_copies", label: "Print required copies", completed: false, completedAt: null, notes: null },
    ],
  },
  {
    key: "identity_verification",
    label: "Identity Verification",
    status: "pending",
    completedAt: null,
    completedBy: null,
    notes: null,
    steps: [
      { key: "verify_id", label: "Verify testator identity with government-issued photo ID", completed: false, completedAt: null, notes: null },
      { key: "record_id_details", label: "Record identification details", completed: false, completedAt: null, notes: null },
      { key: "confirm_independent_arrival", label: "Confirm testator arrived independently", completed: false, completedAt: null, notes: null },
    ],
  },
  {
    key: "capacity_confirmation",
    label: "Capacity Confirmation at Signing",
    status: "pending",
    completedAt: null,
    completedBy: null,
    notes: null,
    steps: [
      { key: "state_name_date", label: "Ask testator to state their name and today's date", completed: false, completedAt: null, notes: null },
      { key: "describe_purpose", label: "Ask testator to describe the purpose of today's meeting", completed: false, completedAt: null, notes: null },
      { key: "confirm_signing_will", label: "Confirm testator understands they are signing a will", completed: false, completedAt: null, notes: null },
      { key: "note_demeanor", label: "Note testator's demeanor and lucidity", completed: false, completedAt: null, notes: null },
    ],
  },
  {
    key: "document_review",
    label: "Final Document Review",
    status: "pending",
    completedAt: null,
    completedBy: null,
    notes: null,
    steps: [
      { key: "review_provisions", label: "Review key provisions with testator", completed: false, completedAt: null, notes: null },
      { key: "confirm_beneficiaries", label: "Confirm beneficiary designations are correct", completed: false, completedAt: null, notes: null },
      { key: "confirm_appointments", label: "Confirm executor/trustee appointments", completed: false, completedAt: null, notes: null },
      { key: "ask_changes", label: "Ask if testator wants any last-minute changes", completed: false, completedAt: null, notes: null },
      { key: "testator_satisfied", label: "Testator confirms satisfaction with all documents", completed: false, completedAt: null, notes: null },
    ],
  },
  {
    key: "execution",
    label: "Will Execution",
    status: "pending",
    completedAt: null,
    completedBy: null,
    notes: null,
    steps: [
      { key: "testator_signs", label: "Testator signs each document", completed: false, completedAt: null, notes: null },
      { key: "testator_initials", label: "Testator initials each page (if required by jurisdiction)", completed: false, completedAt: null, notes: null },
      { key: "witnesses_observe", label: "Witnesses observe testator's signature", completed: false, completedAt: null, notes: null },
    ],
  },
  {
    key: "witnessing",
    label: "Witnessing & Attestation",
    status: "pending",
    completedAt: null,
    completedBy: null,
    notes: null,
    steps: [
      { key: "witness_1_signs", label: "First witness signs attestation clause", completed: false, completedAt: null, notes: null },
      { key: "witness_2_signs", label: "Second witness signs attestation clause", completed: false, completedAt: null, notes: null },
      { key: "notary_signs", label: "Notary signs and affixes seal (if applicable)", completed: false, completedAt: null, notes: null },
      { key: "confirm_all_signatures", label: "All parties confirm signatures are complete", completed: false, completedAt: null, notes: null },
    ],
  },
  {
    key: "post_execution",
    label: "Post-Execution",
    status: "pending",
    completedAt: null,
    completedBy: null,
    notes: null,
    steps: [
      { key: "make_copies", label: "Make copies for all required parties", completed: false, completedAt: null, notes: null },
      { key: "secure_storage", label: "Place original in secure storage", completed: false, completedAt: null, notes: null },
      { key: "log_custody", label: "Log custody chain entry", completed: false, completedAt: null, notes: null },
      { key: "send_confirmation", label: "Send confirmation to testator", completed: false, completedAt: null, notes: null },
      { key: "schedule_review", label: "Schedule next review reminder", completed: false, completedAt: null, notes: null },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (d: string | Date | null) => {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

const formatTime = (t: string | null) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h);
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${suffix}`;
};

const formatTimestamp = (d: Date | string | null) => {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

// ── Component ─────────────────────────────────────────────────────────────────
const SigningCeremonyTab: React.FC<Props> = ({ matterId, caseData, onUpdate }) => {
  const ceremony: SigningCeremony = useMemo(() => {
    const raw = caseData?.signingCeremony;
    if (!raw) {
      return {
        status: "not_scheduled" as const,
        scheduledDate: null,
        scheduledTime: null,
        location: null,
        phases: [],
        attendees: [],
        videoRecorded: false,
        videoConsentObtained: false,
        ceremonyNotes: null,
        completedAt: null,
      };
    }
    return {
      ...raw,
      phases: raw.phases?.length ? raw.phases : [],
    };
  }, [caseData]);

  const phases: Phase[] = useMemo(() => {
    if (ceremony.phases.length > 0) return ceremony.phases;
    return DEFAULT_PHASES;
  }, [ceremony.phases]);

  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>({});
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showStepNoteModal, setShowStepNoteModal] = useState<{ phaseIdx: number; stepIdx: number } | null>(null);
  const [stepNoteText, setStepNoteText] = useState("");
  const [showAttendeesPanel, setShowAttendeesPanel] = useState(true);
  const [newAttendee, setNewAttendee] = useState({ name: "", role: "Witness 1" });
  const [saving, setSaving] = useState(false);

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    scheduledDate: ceremony.scheduledDate ? new Date(ceremony.scheduledDate).toISOString().split("T")[0] : "",
    scheduledTime: ceremony.scheduledTime || "",
    location: ceremony.location || "",
    status: ceremony.status,
    videoRecorded: ceremony.videoRecorded,
    videoConsentObtained: ceremony.videoConsentObtained,
    ceremonyNotes: ceremony.ceremonyNotes || "",
    attendees: [...(ceremony.attendees || [])],
  });

  // ── Save helper ───────────────────────────────────────────────────────────
  const saveCeremony = useCallback(async (updates: Partial<SigningCeremony>) => {
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const updatedCeremony = {
        ...ceremony,
        phases,
        ...updates,
      };
      await estatesApi.update(caseData._id, { signingCeremony: updatedCeremony });
      onUpdate();
    } catch (err) {
      console.error("Failed to save signing ceremony:", err);
    } finally {
      setSaving(false);
    }
  }, [ceremony, phases, caseData._id, onUpdate]);

  // ── Phase & Step handlers ─────────────────────────────────────────────────
  const togglePhase = (key: string) => {
    setExpandedPhases(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleStep = async (phaseIdx: number, stepIdx: number) => {
    const updatedPhases = phases.map((phase, pi) => {
      if (pi !== phaseIdx) return phase;
      const updatedSteps = phase.steps.map((step, si) => {
        if (si !== stepIdx) return step;
        return {
          ...step,
          completed: !step.completed,
          completedAt: !step.completed ? new Date() : null,
        };
      });

      // Auto-determine phase status
      const completedCount = updatedSteps.filter(s => s.completed).length;
      let newStatus = phase.status;
      if (completedCount === 0) {
        newStatus = "pending";
      } else if (completedCount === updatedSteps.length) {
        newStatus = "completed";
      } else {
        newStatus = "in_progress";
      }

      return {
        ...phase,
        steps: updatedSteps,
        status: newStatus as Phase["status"],
        completedAt: newStatus === "completed" ? new Date() : null,
      };
    });

    // Auto-detect overall ceremony status
    const anyInProgress = updatedPhases.some(p => p.status === "in_progress");
    const allDone = updatedPhases.every(p => p.status === "completed" || p.status === "skipped");
    let ceremonyStatus = ceremony.status;
    if (allDone && ceremony.status !== "cancelled") {
      ceremonyStatus = "completed";
    } else if (anyInProgress && ceremony.status !== "cancelled") {
      ceremonyStatus = "in_progress";
    }

    await saveCeremony({
      phases: updatedPhases,
      status: ceremonyStatus,
      completedAt: ceremonyStatus === "completed" ? new Date() : ceremony.completedAt,
    });
  };

  const markPhaseComplete = async (phaseIdx: number) => {
    const updatedPhases = phases.map((phase, pi) => {
      if (pi !== phaseIdx) return phase;
      return {
        ...phase,
        status: "completed" as const,
        completedAt: new Date(),
        steps: phase.steps.map(s => ({
          ...s,
          completed: true,
          completedAt: s.completedAt || new Date(),
        })),
      };
    });

    const allDone = updatedPhases.every(p => p.status === "completed" || p.status === "skipped");
    await saveCeremony({
      phases: updatedPhases,
      status: allDone ? "completed" : "in_progress",
      completedAt: allDone ? new Date() : ceremony.completedAt,
    });
  };

  const skipPhase = async (phaseIdx: number) => {
    const updatedPhases = phases.map((phase, pi) => {
      if (pi !== phaseIdx) return phase;
      return { ...phase, status: "skipped" as const };
    });

    const allDone = updatedPhases.every(p => p.status === "completed" || p.status === "skipped");
    await saveCeremony({
      phases: updatedPhases,
      status: allDone ? "completed" : ceremony.status,
      completedAt: allDone ? new Date() : ceremony.completedAt,
    });
  };

  const savePhaseNotes = async (phaseIdx: number, notes: string) => {
    const updatedPhases = phases.map((phase, pi) => {
      if (pi !== phaseIdx) return phase;
      return { ...phase, notes };
    });
    await saveCeremony({ phases: updatedPhases });
  };

  const openStepNoteModal = (phaseIdx: number, stepIdx: number) => {
    setStepNoteText(phases[phaseIdx].steps[stepIdx].notes || "");
    setShowStepNoteModal({ phaseIdx, stepIdx });
  };

  const saveStepNote = async () => {
    if (!showStepNoteModal) return;
    const { phaseIdx, stepIdx } = showStepNoteModal;
    const updatedPhases = phases.map((phase, pi) => {
      if (pi !== phaseIdx) return phase;
      return {
        ...phase,
        steps: phase.steps.map((step, si) => {
          if (si !== stepIdx) return step;
          return { ...step, notes: stepNoteText || null };
        }),
      };
    });
    await saveCeremony({ phases: updatedPhases });
    setShowStepNoteModal(null);
  };

  // ── Schedule modal handlers ───────────────────────────────────────────────
  const openScheduleModal = () => {
    setScheduleForm({
      scheduledDate: ceremony.scheduledDate ? new Date(ceremony.scheduledDate).toISOString().split("T")[0] : "",
      scheduledTime: ceremony.scheduledTime || "",
      location: ceremony.location || "",
      status: ceremony.status,
      videoRecorded: ceremony.videoRecorded,
      videoConsentObtained: ceremony.videoConsentObtained,
      ceremonyNotes: ceremony.ceremonyNotes || "",
      attendees: [...(ceremony.attendees || [])],
    });
    setShowScheduleModal(true);
  };

  const handleScheduleSave = async () => {
    const updates: Partial<SigningCeremony> = {
      scheduledDate: scheduleForm.scheduledDate || null,
      scheduledTime: scheduleForm.scheduledTime || null,
      location: scheduleForm.location || null,
      status: scheduleForm.status,
      videoRecorded: scheduleForm.videoRecorded,
      videoConsentObtained: scheduleForm.videoConsentObtained,
      ceremonyNotes: scheduleForm.ceremonyNotes || null,
      attendees: scheduleForm.attendees,
    };

    // If scheduling for the first time and status is still not_scheduled, set to scheduled
    if (scheduleForm.scheduledDate && updates.status === "not_scheduled") {
      updates.status = "scheduled";
    }

    await saveCeremony(updates);
    setShowScheduleModal(false);
  };

  const addScheduleAttendee = () => {
    if (!newAttendee.name.trim()) return;
    setScheduleForm(prev => ({
      ...prev,
      attendees: [...prev.attendees, { ...newAttendee, present: false }],
    }));
    setNewAttendee({ name: "", role: "Witness 1" });
  };

  const removeScheduleAttendee = (idx: number) => {
    setScheduleForm(prev => ({
      ...prev,
      attendees: prev.attendees.filter((_, i) => i !== idx),
    }));
  };

  const toggleAttendeePresent = async (idx: number) => {
    const updatedAttendees = (ceremony.attendees || []).map((a, i) => {
      if (i !== idx) return a;
      return { ...a, present: !a.present };
    });
    await saveCeremony({ attendees: updatedAttendees });
  };

  const addAttendeeInline = async () => {
    if (!newAttendee.name.trim()) return;
    const updatedAttendees = [...(ceremony.attendees || []), { ...newAttendee, present: false }];
    await saveCeremony({ attendees: updatedAttendees });
    setNewAttendee({ name: "", role: "Witness 1" });
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const totalSteps = useMemo(() => phases.reduce((sum, p) => sum + p.steps.length, 0), [phases]);
  const completedSteps = useMemo(() => phases.reduce((sum, p) => sum + p.steps.filter(s => s.completed).length, 0), [phases]);
  const overallProgress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  const completedPhaseCount = phases.filter(p => p.status === "completed").length;
  const statusCfg = STATUS_CONFIG[ceremony.status] || STATUS_CONFIG.not_scheduled;
  const isCompleted = ceremony.status === "completed";
  const isCancelled = ceremony.status === "cancelled";
  const isNotScheduled = ceremony.status === "not_scheduled";
  const presentCount = (ceremony.attendees || []).filter(a => a.present).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* ── Header Card ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-purple-50 flex items-center justify-center">
              <PenTool className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Signing Ceremony</h2>
              <p className="text-sm text-slate-500">
                {isCompleted
                  ? "Ceremony completed successfully"
                  : `${completedPhaseCount} of ${phases.length} phases completed`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusCfg.bg} ${statusCfg.color} border ${statusCfg.border}`}>
              {statusCfg.label}
            </span>
            <button
              onClick={openScheduleModal}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors"
            >
              {isNotScheduled ? (
                <><Calendar className="h-4 w-4" /> Schedule Ceremony</>
              ) : (
                <><Edit3 className="h-4 w-4" /> Edit Details</>
              )}
            </button>
          </div>
        </div>

        {/* Scheduled details */}
        {ceremony.scheduledDate && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-6 mt-3 pt-3 border-t border-slate-100"
          >
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Calendar className="h-4 w-4 text-slate-400" />
              <span>{formatDate(ceremony.scheduledDate)}</span>
            </div>
            {ceremony.scheduledTime && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="h-4 w-4 text-slate-400" />
                <span>{formatTime(ceremony.scheduledTime)}</span>
              </div>
            )}
            {ceremony.location && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <MapPin className="h-4 w-4 text-slate-400" />
                <span>{ceremony.location}</span>
              </div>
            )}
            {ceremony.videoRecorded && (
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <Video className="h-4 w-4" />
                <span>Video Recording</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Overall progress bar */}
        {!isNotScheduled && !isCancelled && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
              <span>Overall Progress</span>
              <span className="font-medium">{overallProgress}%</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${isCompleted ? "bg-emerald-500" : "bg-[#0F5132]"}`}
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Completion Summary ────────────────────────────────────────────── */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl shadow-sm border border-emerald-200 p-6"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-emerald-800">Ceremony Completed</h3>
                {ceremony.completedAt && (
                  <p className="text-sm text-emerald-600 mt-0.5">
                    Completed on {formatDate(ceremony.completedAt)}
                  </p>
                )}
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="bg-white/60 rounded-xl p-3 text-center">
                    <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Attendees Present</p>
                    <p className="text-xl font-bold text-emerald-800 mt-1">
                      {presentCount} / {(ceremony.attendees || []).length}
                    </p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3 text-center">
                    <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Phases Completed</p>
                    <p className="text-xl font-bold text-emerald-800 mt-1">
                      {completedPhaseCount} / {phases.length}
                    </p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3 text-center">
                    <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Video Recorded</p>
                    <p className="text-xl font-bold text-emerald-800 mt-1 flex items-center justify-center gap-1">
                      {ceremony.videoRecorded ? (
                        <><Video className="h-5 w-5" /> Yes</>
                      ) : (
                        <><VideoOff className="h-5 w-5 text-slate-400" /> No</>
                      )}
                    </p>
                  </div>
                </div>
                {ceremony.ceremonyNotes && (
                  <div className="mt-4 p-3 bg-white/60 rounded-xl">
                    <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide mb-1">Notes</p>
                    <p className="text-sm text-emerald-800">{ceremony.ceremonyNotes}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Not Scheduled Prompt ──────────────────────────────────────────── */}
      {isNotScheduled && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center"
        >
          <div className="h-16 w-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-8 w-8 text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Schedule the Signing Ceremony</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
            Set a date, time, and location for the will signing ceremony. You can then track each phase of the ceremony workflow.
          </p>
          <button
            onClick={openScheduleModal}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors"
          >
            <Calendar className="h-4 w-4" /> Schedule Ceremony
          </button>
        </motion.div>
      )}

      {/* ── Phase Stepper + Attendees Layout ──────────────────────────────── */}
      {!isNotScheduled && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Phase Stepper (main area) */}
          <div className="lg:col-span-2 space-y-0">
            <div className="relative">
              {phases.map((phase, phaseIdx) => {
                const isExpanded = expandedPhases[phase.key] ?? false;
                const phaseCfg = PHASE_STATUS_CONFIG[phase.status] || PHASE_STATUS_CONFIG.pending;
                const PhaseIcon = PHASE_ICONS[phase.key] || Circle;
                const completedCount = phase.steps.filter(s => s.completed).length;
                const totalCount = phase.steps.length;
                const phaseProgress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
                const isLast = phaseIdx === phases.length - 1;
                const isPhaseComplete = phase.status === "completed";
                const isPhaseSkipped = phase.status === "skipped";

                return (
                  <div key={phase.key} className="relative flex gap-4">
                    {/* Vertical line + circle */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div
                        className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center ring-4 ${phaseCfg.bg} ${phaseCfg.ringColor} transition-all`}
                      >
                        {isPhaseComplete ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        ) : (
                          <PhaseIcon className={`h-5 w-5 ${phaseCfg.color}`} />
                        )}
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 flex-1 min-h-[24px] ${isPhaseComplete ? "bg-emerald-300" : "bg-slate-200"}`} />
                      )}
                    </div>

                    {/* Phase content */}
                    <div className={`flex-1 pb-6 ${isLast ? "pb-0" : ""}`}>
                      <motion.div
                        className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all ${
                          isPhaseComplete ? "border-emerald-200" : isPhaseSkipped ? "border-slate-200 opacity-60" : "border-slate-200"
                        }`}
                      >
                        {/* Phase header */}
                        <button
                          onClick={() => togglePhase(phase.key)}
                          className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-slate-400">
                              {String(phaseIdx + 1).padStart(2, "0")}
                            </span>
                            <h3 className={`font-semibold ${isPhaseSkipped ? "text-slate-400 line-through" : "text-slate-800"}`}>
                              {phase.label}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${phaseCfg.bg} ${phaseCfg.color}`}>
                              {phaseCfg.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            {!isPhaseSkipped && (
                              <span className="text-xs text-slate-400">
                                {completedCount}/{totalCount}
                              </span>
                            )}
                            <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                          </div>
                        </button>

                        {/* Phase progress bar */}
                        {!isPhaseSkipped && (
                          <div className="px-4 pb-2">
                            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <motion.div
                                className={`h-full rounded-full ${isPhaseComplete ? "bg-emerald-500" : "bg-blue-500"}`}
                                initial={{ width: 0 }}
                                animate={{ width: `${phaseProgress}%` }}
                                transition={{ duration: 0.3 }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Expanded content */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 pb-4 space-y-2">
                                {/* Steps checklist */}
                                {phase.steps.map((step, stepIdx) => (
                                  <div
                                    key={step.key}
                                    className={`flex items-start gap-3 p-2.5 rounded-lg transition-colors ${
                                      step.completed ? "bg-emerald-50/50" : "hover:bg-slate-50"
                                    }`}
                                  >
                                    <button
                                      onClick={() => toggleStep(phaseIdx, stepIdx)}
                                      disabled={saving || isCancelled}
                                      className="flex-shrink-0 mt-0.5"
                                    >
                                      {step.completed ? (
                                        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                                      ) : (
                                        <Circle className="h-5 w-5 text-slate-300 hover:text-slate-400 transition-colors" />
                                      )}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm ${step.completed ? "text-slate-500 line-through" : "text-slate-700"}`}>
                                        {step.label}
                                      </p>
                                      <div className="flex items-center gap-3 mt-0.5">
                                        {step.completedAt && (
                                          <span className="text-[11px] text-slate-400">
                                            {formatTimestamp(step.completedAt)}
                                          </span>
                                        )}
                                        {step.notes && (
                                          <span className="text-[11px] text-blue-500 italic truncate max-w-[200px]">
                                            {step.notes}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => openStepNoteModal(phaseIdx, stepIdx)}
                                      className={`flex-shrink-0 p-1 rounded-lg hover:bg-slate-100 transition-colors ${
                                        step.notes ? "text-blue-500" : "text-slate-300 hover:text-slate-400"
                                      }`}
                                      title="Add note"
                                    >
                                      <MessageSquare className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ))}

                                {/* Phase notes */}
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                  <label className="block text-xs font-medium text-slate-500 mb-1">Phase Notes</label>
                                  <textarea
                                    value={phase.notes || ""}
                                    onChange={(e) => savePhaseNotes(phaseIdx, e.target.value)}
                                    rows={2}
                                    placeholder="Add notes for this phase..."
                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                                  />
                                </div>

                                {/* Phase action buttons */}
                                {!isPhaseComplete && !isPhaseSkipped && !isCancelled && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <button
                                      onClick={() => markPhaseComplete(phaseIdx)}
                                      disabled={saving}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 transition-colors disabled:opacity-50"
                                    >
                                      <Check className="h-3.5 w-3.5" /> Mark Complete
                                    </button>
                                    <button
                                      onClick={() => skipPhase(phaseIdx)}
                                      disabled={saving}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-500 rounded-lg text-xs font-medium hover:bg-slate-100 transition-colors disabled:opacity-50"
                                    >
                                      <ChevronRight className="h-3.5 w-3.5" /> Skip Phase
                                    </button>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Attendees Panel (sidebar) ───────────────────────────────────── */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden sticky top-6">
              <button
                onClick={() => setShowAttendeesPanel(prev => !prev)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-500" />
                  <h3 className="font-semibold text-slate-800">Attendees</h3>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
                    {(ceremony.attendees || []).length}
                  </span>
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${showAttendeesPanel ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {showAttendeesPanel && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 space-y-2">
                      {(ceremony.attendees || []).length === 0 && (
                        <p className="text-sm text-slate-400 py-3 text-center">No attendees added yet</p>
                      )}

                      {(ceremony.attendees || []).map((attendee, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-100 hover:border-slate-200 transition-all bg-slate-50/30"
                        >
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                            attendee.present ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                          }`}>
                            {attendee.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{attendee.name}</p>
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-600">
                              {attendee.role}
                            </span>
                          </div>
                          <button
                            onClick={() => toggleAttendeePresent(idx)}
                            disabled={saving}
                            className={`flex-shrink-0 px-2 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                              attendee.present
                                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                            }`}
                          >
                            {attendee.present ? "Present" : "Absent"}
                          </button>
                        </div>
                      ))}

                      {/* Add attendee inline form */}
                      <div className="pt-2 border-t border-slate-100 space-y-2">
                        <div className="flex gap-2">
                          <input
                            value={newAttendee.name}
                            onChange={e => setNewAttendee(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Name"
                            className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </div>
                        <div className="flex gap-2">
                          <select
                            value={newAttendee.role}
                            onChange={e => setNewAttendee(prev => ({ ...prev, role: e.target.value }))}
                            className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                          >
                            {ATTENDEE_ROLES.map(r => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                          <button
                            onClick={addAttendeeInline}
                            disabled={!newAttendee.name.trim() || saving}
                            className="px-3 py-2 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Video recording status */}
                      <div className="pt-3 mt-2 border-t border-slate-100 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Video className="h-4 w-4 text-slate-400" />
                            <span>Video Recording</span>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            ceremony.videoRecorded ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                          }`}>
                            {ceremony.videoRecorded ? "On" : "Off"}
                          </span>
                        </div>
                        {ceremony.videoRecorded && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Shield className="h-4 w-4 text-slate-400" />
                              <span>Consent Obtained</span>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                              ceremony.videoConsentObtained ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                            }`}>
                              {ceremony.videoConsentObtained ? "Yes" : "No"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}

      {/* ── Schedule/Edit Modal ───────────────────────────────────────────── */}
      <PremiumModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title={ceremony.status === "not_scheduled" ? "Schedule Signing Ceremony" : "Edit Ceremony Details"}
        subtitle="Set the date, time, location, and attendees for the signing ceremony"
        icon={<Calendar className="h-5 w-5" />}
        size="xl"
      >
        <div className="p-1 space-y-5">
          {/* Date, time, location */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
              <input
                type="date"
                value={scheduleForm.scheduledDate}
                onChange={e => setScheduleForm(prev => ({ ...prev, scheduledDate: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Time</label>
              <input
                type="time"
                value={scheduleForm.scheduledTime}
                onChange={e => setScheduleForm(prev => ({ ...prev, scheduledTime: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
              <input
                value={scheduleForm.location}
                onChange={e => setScheduleForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Conference Room A"
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Status selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Ceremony Status</label>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setScheduleForm(prev => ({ ...prev, status: key as SigningCeremony["status"] }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                    scheduleForm.status === key
                      ? `${cfg.bg} ${cfg.color} ${cfg.border}`
                      : "bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Attendees manager */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Attendees</label>
            <div className="space-y-2 mb-3">
              {scheduleForm.attendees.map((att, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="flex-1">
                    <span className="text-sm font-medium text-slate-700">{att.name}</span>
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-600">{att.role}</span>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={att.present}
                      onChange={() => {
                        setScheduleForm(prev => ({
                          ...prev,
                          attendees: prev.attendees.map((a, i) =>
                            i === idx ? { ...a, present: !a.present } : a
                          ),
                        }));
                      }}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Present
                  </label>
                  <button
                    onClick={() => removeScheduleAttendee(idx)}
                    className="p-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={newAttendee.name}
                onChange={e => setNewAttendee(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Attendee name"
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                onKeyDown={e => e.key === "Enter" && addScheduleAttendee()}
              />
              <select
                value={newAttendee.role}
                onChange={e => setNewAttendee(prev => ({ ...prev, role: e.target.value }))}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              >
                {ATTENDEE_ROLES.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <button
                onClick={addScheduleAttendee}
                disabled={!newAttendee.name.trim()}
                className="px-3 py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Video toggles */}
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:border-slate-200 transition-colors">
              <input
                type="checkbox"
                checked={scheduleForm.videoRecorded}
                onChange={e => setScheduleForm(prev => ({ ...prev, videoRecorded: e.target.checked }))}
                className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <div>
                <div className="flex items-center gap-1.5">
                  <Video className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Video Recording</span>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">Record the signing ceremony</p>
              </div>
            </label>
            <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              scheduleForm.videoRecorded ? "bg-slate-50 border-slate-100 hover:border-slate-200" : "bg-slate-50/50 border-slate-100 opacity-50 cursor-not-allowed"
            }`}>
              <input
                type="checkbox"
                checked={scheduleForm.videoConsentObtained}
                disabled={!scheduleForm.videoRecorded}
                onChange={e => setScheduleForm(prev => ({ ...prev, videoConsentObtained: e.target.checked }))}
                className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <div>
                <div className="flex items-center gap-1.5">
                  <Shield className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Consent Obtained</span>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">All parties consent to recording</p>
              </div>
            </label>
          </div>

          {/* Ceremony notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ceremony Notes</label>
            <textarea
              value={scheduleForm.ceremonyNotes}
              onChange={e => setScheduleForm(prev => ({ ...prev, ceremonyNotes: e.target.value }))}
              rows={3}
              placeholder="Any special instructions or notes for the ceremony..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            />
          </div>

          {/* Modal footer */}
          <div className="flex justify-between pt-4 border-t border-slate-100">
            <button
              onClick={() => setShowScheduleModal(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleScheduleSave}
              disabled={!scheduleForm.scheduledDate || saving}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : ceremony.status === "not_scheduled" ? "Schedule Ceremony" : "Save Changes"}
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Step Note Modal ───────────────────────────────────────────────── */}
      <PremiumModal
        open={showStepNoteModal !== null}
        onClose={() => setShowStepNoteModal(null)}
        title="Step Note"
        subtitle={showStepNoteModal ? phases[showStepNoteModal.phaseIdx]?.steps[showStepNoteModal.stepIdx]?.label : ""}
        icon={<MessageSquare className="h-5 w-5" />}
        size="sm"
      >
        <div className="p-1 space-y-4">
          <textarea
            value={stepNoteText}
            onChange={e => setStepNoteText(e.target.value)}
            rows={4}
            placeholder="Add a note for this step..."
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            autoFocus
          />
          <div className="flex justify-between pt-2 border-t border-slate-100">
            <button
              onClick={() => setShowStepNoteModal(null)}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveStepNote}
              disabled={saving}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : "Save Note"}
            </button>
          </div>
        </div>
      </PremiumModal>
    </div>
  );
};

export default SigningCeremonyTab;
